<script setup lang="ts">
import { computed, ref } from 'vue'
import { logApiError } from '~/utils/logger'

// Get current user for email auto-fill
const { user, isAuthenticated } = useUser()

// SEO Configuration
const { setSeoMeta, getBreadcrumbSchema } = useSeo()

setSeoMeta({
  title: 'ZwemFabrik',
  description: 'Leer zwemmen bij \'t SpeelFabrik! Professionele zwemlessen voor kinderen in een veilige en leuke omgeving. Van beginners tot gevorderden.',
  image: '/images/og-zwemmen.jpg',
  keywords: ['zwemlessen kinderen', 'leren zwemmen', 'zwemles', 'kinderzwemmen', 'zwemlessen België'],
})

// Branded OG image with custom background
defineOgImage('SpeelFabrik', {
  title: 'ZwemFabrik',
  description: 'Leer zwemmen bij \'t SpeelFabrik! Professionele zwemlessen voor kinderen.',
  backgroundImage: '/images/zwemmen.png',
})

useSchemaOrg([
  getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Zwemmen', path: '/zwemmen' },
  ]),
])

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'ZwemFabrik',
    subtitle: 'met plezier en vertrouwen in het water',
    icon: 'bx-swim',
    iconColor: 'info',
  },
})

// Form state
const parentName = ref('')
const childName = ref('')
const childAge = ref<number | null>(null)
const manualEmail = ref('')
const zwemType = ref('')
const swimmingLevel = ref('')
const importantInfo = ref('')
const preferredTimeSlot = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Use authenticated user's email or manual input
const email = computed(() => isAuthenticated.value ? user.value?.email || '' : manualEmail.value)

const zwemTypes = [
  { value: 'zwemmen_woensdag', title: 'Zwemmen woensdag' },
]

const swimmingLevels = [
  { value: 'weinig_ervaring', title: 'Nog weinig ervaring' },
  { value: 'watergewenning', title: 'Watergewenning gevolgd' },
  { value: 'zwemles_gevolgd', title: 'Al zwemles gevolgd' },
  { value: 'kent_zwemslagen', title: 'Kent al zwemslagen' },
]

const timeSlots = [
  { value: '14u20 - 14u50', title: '14u20 - 14u50' },
  { value: '14u50 - 15u20', title: '14u50 - 15u20' },
  { value: '15u20 - 15u50', title: '15u20 - 15u50' },
  { value: 'geen_voorkeur', title: 'Geen voorkeur' },
]

const handleSubmit = async () => {
  // Reset messages
  successMessage.value = ''
  errorMessage.value = ''

  // Basic validation
  if (!parentName.value || !childName.value || !childAge.value || !zwemType.value || !swimmingLevel.value || !email.value) {
    errorMessage.value = 'Vul alle verplichte velden in'

    return
  }

  // Validate time slot for woensdag option
  if (zwemType.value === 'zwemmen_woensdag' && !preferredTimeSlot.value) {
    errorMessage.value = 'Selecteer een voorkeur van zwemblok'

    return
  }

  isLoading.value = true

  try {
    const response = await $fetch('/api/swimming/register', {
      method: 'POST',
      body: {
        parent_name: parentName.value,
        child_name: childName.value,
        child_age: childAge.value,
        email: email.value,
        zwem_type: zwemType.value,
        swimming_level: swimmingLevel.value,
        important_info: importantInfo.value,
        preferred_time_slot: zwemType.value === 'zwemmen_woensdag' ? preferredTimeSlot.value : null,
      },
    })

    // Show success message
    successMessage.value = response.message || 'Je aanmelding is succesvol verzonden!'

    // Clear form
    parentName.value = ''
    childName.value = ''
    childAge.value = null
    manualEmail.value = ''
    zwemType.value = ''
    swimmingLevel.value = ''
    importantInfo.value = ''
    preferredTimeSlot.value = ''
  }
  catch (error) {
    logApiError('/api/swimming/register', error)

    const err = error as { data?: { message?: string }, message?: string }

    errorMessage.value = err.data?.message || err.message || 'Er is iets misgegaan. Probeer het opnieuw.'
  }
  finally {
    isLoading.value = false
  }
}
</script>

<template>
  <VContainer>
    <VRow>
      <!-- Information Card -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardText class="text-center pa-8">
            <VIcon icon="bx-swim" size="64" color="info" class="mb-4" />
            <h2 class="text-h4 mb-4">
              Zwemlessen voor kinderen
            </h2>
            <p class="text-body-1 text-medium-emphasis text-start">
              Omdat wij zwemlessen ontzettend belangrijk vinden, zijn we achter de schermen volop op zoek naar een nieuw zwembad ter vervanging van Geerdegemvaart, waar onze privézwemlessen doorgingen.
              Voorlopig nemen we tijdens de zomervakantie even een duikpauze, waardoor er geen zwemlessen zullen plaatsvinden. We hopen in september opnieuw het water in te kunnen springen op een nieuwe of andere locatie.
              Zodra we meer nieuws hebben, laten we dat natuurlijk meteen weten. Tot die tijd kunnen er helaas geen nieuwe aanmeldingen voor zwemlessen worden aangenomen.
              Hou onze ‘socials’ dus zeker goed in de gaten!
            </p>
            <p class="text-body-1 text-medium-emphasis text-start mt-4">
              Wij kijken er alvast naar uit weer heel wat kindjes plezier, veiligheid en vertrouwen in het water te laten beleven.
              Graag tot snel!
            </p>
          </VCardText>
          <!-- TODO: tijdelijk verborgen -->
          <VCardText v-if="false" class="text-center pa-8">
            <VIcon icon="bx-swim" size="64" color="info" class="mb-4" />
            <h2 class="text-h4 mb-4">
              Zwemlessen voor kinderen
            </h2>
            <p class="text-body-1 text-medium-emphasis text-start">
              In het zwembad Geerdegemvaart te Mechelen bieden wij zwemlessen aan voor kinderen, van eerste watergewenning tot het aanleren en verfijnen van zwemslagen. Elk kind leert op zijn of haar eigen tempo, met aangepaste oefeningen die aansluiten bij het niveau en de noden van het kind.
            </p>
            <p class="text-body-1 text-medium-emphasis text-start mt-4">
              De lessen gaan door in kleine groepjes van maximum 3 kinderen, zodat er veel persoonlijke begeleiding en aandacht is. Elke les duurt 30 minuten en wordt gegeven in reeksen van 10 lesjes.
              Er is de mogelijkheid om te zwemmen op woensdagnamiddag aan 171 euro voor een lessenreeks van 10 lessen.<br>
            </p>
            <p class="text-body-1 text-medium-emphasis text-start mt-4">
              Of je kind nu een echte waterrat is of net watervrees heeft: wij maken er ons werk van om elk kind uit te dagen, te motiveren en zich vooral veilig en op zijn gemak te laten voelen in het water. Spel, vertrouwen en plezier staan centraal.
            </p>
            <p class="text-body-1 text-medium-emphasis text-start mt-4">
              Alle lessen zijn verzekerd via VZW ’t SpeelFabrik, zodat ouders met een gerust hart hun kind kunnen laten deelnemen.
            </p>
            <p class="text-body-1 font-weight-medium text-start mt-4">
              Samen bouwen we stap voor stap aan waterplezier en zwemvaardigheid!
            </p>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Registration Form -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardItem class="pb-0">
            <h4 class="text-h4 mb-1">
              Contacteer ons
            </h4>
          </VCardItem>

          <VCardText>
            <p class="mb-6">
              Vul onderstaand formulier in om contact met ons op te nemen.
            </p>

            <!-- Success Message -->
            <VAlert
              v-if="successMessage" type="success" variant="tonal" class="mb-6" closable
              @click:close="successMessage = ''"
            >
              {{ successMessage }}
            </VAlert>

            <!-- Error Message -->
            <VAlert
              v-if="errorMessage" type="error" variant="tonal" class="mb-6" closable
              @click:close="errorMessage = ''"
            >
              {{ errorMessage }}
            </VAlert>

            <VForm @submit.prevent="handleSubmit">
              <VRow>
                <VCol cols="12">
                  <VTextField
                    v-model="parentName" label="Naam ouder" :disabled="isLoading"
                    required
                  />
                </VCol>

                <VCol cols="12">
                  <VTextField
                    v-if="!isAuthenticated"
                    v-model="manualEmail"
                    label="E-mailadres"
                    type="email"
                    :disabled="isLoading"
                    required
                  />
                  <VTextField
                    v-else
                    :model-value="user?.email || ''"
                    label="E-mailadres"
                    type="email"
                    readonly
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    hint="Je bent ingelogd, je e-mailadres is reeds gekend"
                    persistent-hint
                  />
                </VCol>

                <VCol cols="12" md="8">
                  <VTextField
                    v-model="childName" label="Naam kind" :disabled="isLoading"
                    required
                  />
                </VCol>

                <VCol cols="12" md="4">
                  <VTextField
                    v-model.number="childAge" label="Leeftijd kind" type="number" min="3"
                    max="18" :disabled="isLoading" required
                  />
                </VCol>

                <VCol cols="12">
                  <VSelect
                    v-model="swimmingLevel" :items="swimmingLevels" label="Zwemervaring"
                    placeholder="Kies het niveau" :disabled="isLoading" required
                  />
                </VCol>

                <VCol cols="12">
                  <VSelect
                    v-model="zwemType" :items="zwemTypes" label="Lesmoment"
                    placeholder="Kies het type" :disabled="isLoading" required
                  />
                </VCol>

                <VCol v-if="zwemType === 'zwemmen_woensdag'" cols="12">
                  <VSelect
                    v-model="preferredTimeSlot" :items="timeSlots" label="Voorkeur van zwemblok op woensdag"
                    placeholder="Kies een tijdslot" :disabled="isLoading" required
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="importantInfo"
                    rows="4"
                    label="Belangrijke info (optioneel)" :disabled="isLoading"
                  />
                </VCol>

                <VCol>
                  <VBtn type="submit" :loading="isLoading" :disabled="isLoading" color="info">
                    <VIcon icon="bx-send" class="me-2" />
                    Inschrijven
                  </VBtn>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>
